import { useEffect, useState, useCallback, useRef } from 'react';

import type { Provider } from '@atlaskit/collab-provider';
import { createSocketIOCollabProvider } from '@atlaskit/collab-provider/socket-io-provider';
import type { PresenceActivity } from '@atlaskit/editor-common/collab';

import { useNativeCollabPresenceState } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState';
import { fetchCollabPermissionToken } from '@confluence/native-collab/entry-points/CollabPermissionsToken';
import { type CollabEditState } from '@confluence/native-collab/entry-points/commonCollabTypes';
import { useIsGuest, useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import { useParticipants, usePresenceStoreActions } from '../presence-store';
import {
	CURRENT_USER_DUMMY_SESSION_ID,
	getPresenceServiceUrl,
	getPresenceClientId,
	getPresenceServicePath,
} from '../presenceUtils';
import type { PresenceContentMode, PresencePageContentType } from '../presenceTypes';
import { usePresenceLimiter } from '../presence/usePresenceLimiter';

import { fetchPresenceAGGUsers } from './fetchPresenceAGGUsers';
import { fetchPresenceConfluenceUsers } from './fetchPresenceConfluenceUsers';
import { useAnonymousAsset } from './useAnonymousAsset';

const MAX_FETCH_SIZE = 25;

export const usePagePresence = (
	contentId: string,
	contentType: PresencePageContentType,
	contentMode: PresenceContentMode,
	activity: PresenceActivity,
) => {
	const shouldCreatePresenceConnection = contentMode === 'view';

	const [{ collabEditInitialized, collabEditProvider }] = useNativeCollabState();
	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();
	const { add, remove, addTelepointerData } = usePresenceStoreActions();
	const participants = useParticipants();
	const { cloudId, activationId, userId, isLoggedIn } = useSessionData();
	const isLicensed = useIsLicensed();
	const isGuest = useIsGuest();
	const { fetchAnonymousAsset } = useAnonymousAsset();

	const sessions = useRef<Set<string>>(new Set());
	const providerRef = useRef<Provider | (CollabEditState & Provider) | null>(collabEditProvider);
	const presenceIdRef = useRef<string>(presenceId);
	// really for testing purposes
	const [isInitialized, setIsInitialized] = useState(false);
	const [networkError, setNetworkError] = useState(undefined);

	usePresenceLimiter({
		contentId,
		contentType,
		contentMode,
		participants,
		provider: providerRef.current,
	});

	const getUsers = useCallback(
		(userIds: string[]) => {
			return isGuest || !isLoggedIn || !isLicensed
				? fetchPresenceAGGUsers(userIds)
				: fetchPresenceConfluenceUsers(userIds);
		},
		[isGuest, isLoggedIn, isLicensed],
	);

	const fetchAnonymousAssetCallback = useCallback(
		async (id: string | undefined) => {
			try {
				return await fetchAnonymousAsset(id);
			} catch (error) {
				handleOnFetchError(error);
			}

			return undefined;
		},
		[fetchAnonymousAsset],
	);

	const getTotalParticipants = () => {
		const uniqueSet = new Set();
		// really just needed for current user id before socket connects, but keeping it simple and just iterate though all participants
		participants.forEach((p) => uniqueSet.add(p.presenceId));
		// participants from connection
		providerRef.current?.getParticipants().forEach((p) => uniqueSet.add(p.presenceId));
		return uniqueSet.size;
	};

	const handleOnFetchError = (error: any) => {
		// want to prevent an infinite spam of api calls being made
		setNetworkError(error);
		markErrorAsHandled(error);
	};

	const handleOnPresence = useCallback(
		() => {
			const people = providerRef.current?.getParticipants().filter((p) => p.isHydrated);

			people?.forEach((participant) => {
				sessions.current.add(participant.sessionId);
				add(participant);
			});

			const participantSessions = people?.map((p) => p.sessionId) ?? [];

			// getParticipants returns all active users, so need to determine which ones to remove
			sessions.current.forEach((sessionId) => {
				if (!participantSessions.includes(sessionId)) {
					remove(sessionId);
					sessions.current.delete(sessionId);
				}
			});
		},
		// don't let this depend on anything with frequent state changes to avoid unnecessary subscriptions and un-subscriptions of the event handling
		[add, remove],
	);

	const initializeProvider = useCallback(() => {
		const permissionTokenRefresh = fetchCollabPermissionToken(contentId, undefined);

		const documentAri = `ari:cloud:confluence:${cloudId}:${contentType}/${contentId}`;

		// In order to support deduplication of the collaborator, presenceId will be set to the userId if it exists.
		// This means that anonymous users will still rely on presenceId and not get deduplicated.
		const collaboratorPresenceId = userId ?? presenceIdRef.current;

		const provider = createSocketIOCollabProvider({
			url: getPresenceServiceUrl(),
			path: getPresenceServicePath(cloudId, activationId),
			documentAri,
			permissionTokenRefresh,
			need404: true,
			isPresenceOnly: true,
			productInfo: {
				product: 'confluence',
				subProduct: 'team-presence',
			},
			batchProps: {
				batchSize: MAX_FETCH_SIZE,
				participantsLimit: undefined,
				getUsers,
				onError: handleOnFetchError,
			},
			fetchAnonymousAsset: fetchAnonymousAssetCallback,
			presenceId: collaboratorPresenceId,
			presenceActivity: 'viewer',
		});

		// Since we moved to only creating a presence connection on view pages, we should always be a viewer
		provider.setupForPresenceOnly(getPresenceClientId());

		providerRef.current = provider;
		setIsInitialized(true);
	}, [
		cloudId,
		activationId,
		contentId,
		contentType,
		fetchAnonymousAssetCallback,
		getUsers,
		userId,
	]);

	useEffect(() => {
		if (shouldCreatePresenceConnection) {
			initializeProvider();
		} else {
			setIsInitialized(true);
		}

		// When we don't have a provider and we are not creating a presence connection,
		// we should use the existing collabEditProvider, there was an edge case where this provider wasn't
		// available on the first render.
		if (!providerRef.current && collabEditProvider) {
			providerRef.current = collabEditProvider;
		}

		providerRef.current?.on('presence', handleOnPresence);
		providerRef.current?.on('presence:changed', handleOnPresence);
		providerRef.current?.on('telepointer', addTelepointerData);

		return () => {
			providerRef.current?.off('presence', handleOnPresence);
			providerRef.current?.off('presence:changed', handleOnPresence);
			providerRef.current?.off('telepointer', addTelepointerData);

			// we do not want to destroy the live connection, since we didn't create it
			if (shouldCreatePresenceConnection) {
				providerRef.current?.destroy();
			}
		};
	}, [
		addTelepointerData,
		collabEditProvider,
		shouldCreatePresenceConnection,
		initializeProvider,
		handleOnPresence,
	]);

	useEffect(() => {
		if (shouldCreatePresenceConnection || !collabEditInitialized) {
			return;
		}

		providerRef.current?.sendMessage({
			type: 'participant:activity',
			activity,
		});
	}, [activity, collabEditInitialized, shouldCreatePresenceConnection]);

	useEffect(() => {
		presenceIdRef.current = presenceId;
	}, [presenceId]);

	return {
		// used only for testing
		isInitialized,
		// used only for testing
		provider: providerRef.current,
		presenceId,
		// want to return DUMMY as a default to preserve activity lookup if provider isn't available yet
		sessionId: providerRef.current?.getSessionId() ?? CURRENT_USER_DUMMY_SESSION_ID,
		networkError,
		totalParticipants: getTotalParticipants(),
	};
};
